// QR placeholder resolver for the send-email PREVIEW only.
//
// The saved email HTML (from `parse-email.ts:parseQRCode`) carries
// backend-only markers that the live send resolves server-side
// (main-consumer `qr-placeholder.ts`). The preview iframe never hits the
// backend, so without this helper the markers reach the browser as a
// dead `<img src>` (broken-image icon). Two forms — same as the backend:
//   {{qrCodeImage}}        — the guest's signed check-in token
//   {{qrCodeImage:VALUE}}  — VALUE verbatim, with nested {{var}} resolved
//
// The marker grammar itself (constants + the brace-balanced scanner)
// lives in `qr-markers.ts`, which stays free of the `qrcode` dependency
// so the template galleries can reuse it for their look-alike tiles.
//
// MUST run BEFORE `replaceVariablesInHtml`, otherwise that generic
// replacer collapses the plain `{{qrCodeImage}}` marker to "" (img src=""
// → broken icon) and eats the nested {{var}} inside the custom form
// before we can encode it. Mirrors the ordering of the consumer.

import QRCode from "qrcode";
import { Guest } from "@/types/Guest";
import { fetchGuestQrToken } from "@/lib/api/clients/GuestClient";
import { replaceVariablesInText } from "@/lib/utils/variable-replacer";
import {
  QR_PLAIN_MARKER as PLAIN_MARKER,
  QR_QUIET_ZONE_MODULES,
  findQrCustomMatches as findCustomMatches,
  findQrStyledMatches,
  hasQrMarker,
  decodeQrMarkerValue,
  qrPreviewSvg,
  qrStyleToCssHex,
  type QrStyle,
} from "@/lib/utils/qr-markers";

// Pinned to match main-consumer `qr-placeholder.ts` (QR_ECC / QR_MARGIN /
// QR_TARGET_PX) so the preview QR is pixel-equivalent to the delivered email.
//
// The size is reached through an INTEGER `scale`, computed per code below,
// never through `width`: `qrcode` lets `width` win over `scale` and then
// stretches the module grid to exactly 512 px — 512 / 31 = 16.52 px per
// module, so neighbouring modules came out 16 and 17 px wide. The consumer
// rasterises `floor(512 / (modules + 2 × quiet zone))` px per module (481 px
// for a guest token behind the 4-module quiet zone) and so does this.
const QR_ECC: QRCode.QRCodeErrorCorrectionLevel = "M";
const QR_MARGIN = QR_QUIET_ZONE_MODULES; // quiet zone in modules, painted in the background colour
const QR_TARGET_PX = 512; // approximate output edge length
const QR_RENDER_OPTS: QRCode.QRCodeToDataURLOptions = {
  errorCorrectionLevel: QR_ECC,
  margin: QR_MARGIN,
  color: { dark: "#000000", light: "#ffffff" },
};

/** The consumer's module scale for this content at this ECC level. */
function integerScale(
  content: string,
  errorCorrectionLevel: QRCode.QRCodeErrorCorrectionLevel,
): number {
  const modules = QRCode.create(content, { errorCorrectionLevel }).modules.size;
  return Math.max(1, Math.floor(QR_TARGET_PX / (modules + 2 * QR_MARGIN)));
}

const NESTED_VAR = /\{\{([a-zA-Z0-9_.]+)\}\}/g;

// Neutral "a QR will appear here" tile — used when no guest/token is
// available (template preview, missing guestId, capability denied, or a
// failed fetch). A real look-alike beats a broken-image icon.
const PLACEHOLDER_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(qrPreviewSvg(512, "#c4c4c4", "#f5f5f5"));

// The same tile in the AUTHOR's colours — a styled block whose token could not
// be minted still has to show the pair the author picked, or the preview says
// "grey" about a code the mail will send in red on blue.
function placeholderUri(style?: QrStyle): string {
  if (!style) return PLACEHOLDER_URI;
  return (
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      qrPreviewSvg(512, qrStyleToCssHex(style.fg), qrStyleToCssHex(style.bg)),
    )
  );
}

/** A 403 from the token endpoint: the operator may not mint this guest's token. */
function isForbidden(e: unknown): boolean {
  const err = e as { status?: unknown; response?: { status?: unknown } } | null;
  return err?.status === 403 || err?.response?.status === 403;
}

async function renderDataUri(content: string, style?: QrStyle): Promise<string> {
  // 8-digit hex is what `qrcode` wants for an alpha channel — the same
  // RRGGBBAA the styled marker carries, so a cleared background previews
  // transparent here exactly as the consumer and the backend now render it.
  const ecc = style?.ecc ?? QR_ECC;
  const opts: QRCode.QRCodeToDataURLOptions = {
    ...QR_RENDER_OPTS,
    errorCorrectionLevel: ecc,
    // `create` throws for content past the largest QR version, exactly where
    // `toDataURL` would; every caller already catches and falls back to the
    // neutral tile.
    scale: integerScale(content, ecc),
    ...(style
      ? { color: { dark: qrStyleToCssHex(style.fg), light: qrStyleToCssHex(style.bg) } }
      : {}),
  };
  return QRCode.toDataURL(content, opts);
}

// Signed tokens are deterministic per (event, guest) and stable for the
// session, so cache them to avoid refetching on every preview re-render.
// Only successful fetches are cached; failures fall through to the
// placeholder and may be retried.
const tokenCache = new Map<string, string>();

async function getGuestToken(eventId: string, guestId: string): Promise<string> {
  const key = `${eventId}:${guestId}`;
  const cached = tokenCache.get(key);
  if (cached) return cached;
  const token = await fetchGuestQrToken(eventId, guestId);
  tokenCache.set(key, token);
  return token;
}

function resolveNestedVars(value: string, guest: Guest | null): string {
  if (!guest) return value;
  // replaceVariablesInText leaves unknown {{var}} as "" — match that here
  // by going through the shared replacer, then strip any leftover tokens.
  const resolved = replaceVariablesInText(value, guest);
  return resolved.replace(NESTED_VAR, "");
}

/**
 * Resolve every {{qrCodeImage(:VALUE)?}} marker in `html` to an inline
 * PNG data-URI for the preview. Markers that can't be resolved to a real
 * code fall back to a neutral placeholder tile — never a bare marker.
 *
 * Returns the input unchanged when no marker is present (cheap path for
 * the 90%+ of templates without a QR block).
 */
export async function resolveQrPlaceholdersForPreview(
  html: string,
  opts: {
    guest: Guest | null;
    eventId: string | undefined;
    // When set, a plain {{qrCodeImage}} marker that can't mint a real signed
    // token (no guest/event — e.g. the operator's sample PDF download) renders
    // a REAL scannable QR from this mock value instead of the neutral grey
    // tile, matching the live editor (which draws a mock QR for guestQr too).
    // Also the fallback when the token endpoint REFUSES (403) — and ONLY
    // then: the author still sees a code in their colours, and
    // `onGuestTokenForbidden` lets the dialog say that the real one is minted
    // per recipient. Any other failure paints the neutral tile.
    mockGuestQrValue?: string;
    // Called once per resolve when the signed-token fetch answered 403 — the
    // operator may not mint this guest's check-in token (the single-guest
    // dialog previews with the recipient as the sample guest). Never for any
    // other failure: only a denial is a statement about the recipient.
    onGuestTokenForbidden?: () => void;
  },
): Promise<string> {
  if (!html) return html;
  if (!hasQrMarker(html)) return html;

  const { guest, eventId, mockGuestQrValue, onGuestTokenForbidden } = opts;
  let out = html;
  let forbiddenReported = false;

  // The guest's real code, or — when the endpoint REFUSES (403) — the mock
  // payload in the same style, so the preview keeps showing the author's
  // colours while the badge says the real one is minted per recipient. Any
  // other failure (a 500, the network) throws and every caller paints the
  // look-alike tile, as before the badge existed: a real-looking code with
  // nothing said about it would be the one thing the preview must not show
  // (audit r1 lens 2 LOW-6).
  const guestCodeUri = async (gid: string, eid: string, style?: QrStyle): Promise<string> => {
    try {
      return await renderDataUri(await getGuestToken(eid, gid), style);
    } catch (e) {
      if (!isForbidden(e)) throw e;
      if (!forbiddenReported) {
        forbiddenReported = true;
        onGuestTokenForbidden?.();
      }
      if (mockGuestQrValue) return renderDataUri(mockGuestQrValue, style);
      throw e;
    }
  };

  // Styled forms first — one pass covering both the guest-QR and the custom
  // variant, since the colour pair is the only thing that differs from the
  // legacy passes below.
  for (const { match, style, value } of findQrStyledMatches(out)) {
    let uri = placeholderUri(style);
    try {
      if (value !== null) {
        const resolved = resolveNestedVars(decodeQrMarkerValue(value), guest);
        if (resolved) uri = await renderDataUri(resolved, style);
      } else if (guest?.guestId && eventId) {
        uri = await guestCodeUri(guest.guestId, eventId, style);
      } else if (mockGuestQrValue) {
        uri = await renderDataUri(mockGuestQrValue, style);
      }
    } catch {
      uri = placeholderUri(style);
    }
    out = out.split(match).join(uri);
  }

  // Custom form. Scans `out`, not `html`: the styled pass above has already
  // rewritten its own matches, and a legacy marker nested inside a styled VALUE
  // went with it — scanning the original re-encodes it for nothing. The
  // consumer's expander and the thumbnail lane were both fixed the same way.
  for (const { match, value } of findCustomMatches(out)) {
    // Decoded BEFORE nested variables are resolved, exactly like both
    // expanders — otherwise the preview and the sent mail disagree.
    const resolved = resolveNestedVars(decodeQrMarkerValue(value), guest);
    let uri: string;
    try {
      uri = resolved ? await renderDataUri(resolved) : PLACEHOLDER_URI;
    } catch {
      uri = PLACEHOLDER_URI;
    }
    out = out.split(match).join(uri);
  }

  // Plain form — needs guestId + a signed token from the backend. Falls back to
  // a real mock QR (if a mock value was supplied) and then the neutral tile.
  if (out.includes(PLAIN_MARKER)) {
    let uri = PLACEHOLDER_URI;
    if (guest?.guestId && eventId) {
      try {
        uri = await guestCodeUri(guest.guestId, eventId);
      } catch {
        uri = PLACEHOLDER_URI;
      }
    } else if (mockGuestQrValue) {
      try {
        uri = await renderDataUri(mockGuestQrValue);
      } catch {
        uri = PLACEHOLDER_URI;
      }
    }
    out = out.split(PLAIN_MARKER).join(uri);
  }

  return out;
}
